/**
 * Which slot a food may be placed in.
 *
 * A placement's category is stored on `lunch_items`, separately from the
 * item's own -- deliberately, per the note at the top of 0001_init.sql -- so
 * the question "may this food go in that slot" is a real one with more than
 * one defensible answer. This is the answer, and it is the only one: the
 * board, the pantry dimming, the picker and the API all call it, so a food
 * that looks droppable is droppable and one that does not is refused.
 *
 * The rule: its own category always, plus any category sharing a non-empty
 * `interchangeableGroup` with it (see 0011). Side and Snack share one because
 * they differ by container, not by kind of food. Main, Fruit and Veg share
 * none, so they stay strict.
 */

export interface Placeable {
  id: string
  interchangeableGroup: string
}

/** Every category that accepts a food filed under `categoryId`, itself included. */
export function acceptingCategoryIds(categories: Placeable[], categoryId: string): Set<string> {
  const ids = new Set([categoryId])
  const group = categories.find((c) => c.id === categoryId)?.interchangeableGroup ?? ''
  if (group !== '') {
    for (const c of categories) if (c.interchangeableGroup === group) ids.add(c.id)
  }
  return ids
}

/**
 * May a food filed under `itemCategoryId` be placed in `targetCategoryId`?
 *
 * An unknown category answers false rather than true: a category id that is
 * not in the list is a bug or a guess, and neither should open a slot.
 */
export function canPlace(
  categories: Placeable[],
  itemCategoryId: string,
  targetCategoryId: string,
): boolean {
  if (itemCategoryId === targetCategoryId) return true
  if (!categories.some((c) => c.id === targetCategoryId)) return false
  return acceptingCategoryIds(categories, itemCategoryId).has(targetCategoryId)
}
